import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select/creatable";
import React, { useRef } from "react";
import ReactDatePicker from "react-datepicker";
import {
  AdminSharedInsightsProductInfo,
  Product,
  SharedInsightsProduct,
  SharedInsightsProductInput,
} from "../../graphql/fragments/product";
import { MarketplaceControlsProps } from "./components/MarketplaceControls";
import { RouteComponentProps, withRouter } from "react-router";
import { Mutation, MutationFn, withApollo } from "react-apollo";
import gql from "graphql-tag";
import TopicSelect, { TopicSelectValue } from "./components/TopicSelect";
import moment from "moment";
import { ProductType } from "./components/ProductTypeSelect";
import axios from "axios";
import ApolloClient from "apollo-client";

interface SharedProductFormProps extends RouteComponentProps {
  mutation: CreateUpdateSharedInsightsMutation;
  product: SharedInsightsProduct | null;
  marketplaceFlags: MarketplaceControlsProps["marketplaceFlags"];
  productImageFile: File | null;
  productType: ProductType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: ApolloClient<any>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const omitTypename = (obj: any) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename, ...rest } = obj;
  return rest;
};

const createEmptyFileList = (name: string) => {
  const list = new DataTransfer();
  const file = new File([""], name);
  list.items.add(file);
  return list.files;
};

const getResponse = (
  mutationResult: CreateUpdateSharedInsightsMutationResult
) => {
  if ("createSharedInsightsProduct" in mutationResult) {
    return mutationResult.createSharedInsightsProduct;
  }
  if ("updateSharedInsightsProduct" in mutationResult) {
    return mutationResult.updateSharedInsightsProduct;
  }
  return null;
};

const SharedProductForm = (props: SharedProductFormProps) => {
  const {
    product,
    mutation,
    marketplaceFlags,
    productType,
    history,
    productImageFile,
    client,
  } = props;

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    watch,
  } = useForm<SharedProductFormInput>({
    defaultValues: {
      name: product?.name ?? "",
      description: product?.description ?? "",
      previewDescription: product?.previewDescription ?? "",
      isAvailable: product?.isAvailable ?? true,
      publishDate: product?.publishDate ?? moment().toISOString(),
      vendors: product?.vendors.map(omitTypename) ?? [],
      sectors: product?.sectors.map(omitTypename) ?? [],
      details: {
        eventCount: product?.details.eventCount ?? "0",
        pageCount: product?.details.pageCount ?? "0",
        eventStartDate: product?.details.eventStartDate ?? null,
        eventEndDate: product?.details.eventEndDate ?? null,
        tags: product?.details.tags ?? [],
        teaserQuotes: product?.details.teaserQuotes ?? [],
      },
      files: product?.files ? createEmptyFileList(product.files[0]) : null,
    },
  });
  const toast = useToast({ duration: 9000, isClosable: true });
  const toastUnknownError = () => {
    toast({
      title: "Error",
      description: "An unknown error occurred.",
      status: "error",
    });
  };

  const watchFiles = watch("files");
  const fileInputRef = useRef<HTMLInputElement | null>();
  const { ref: fileCallbackRef, ...fileRegisterRest } = register("files");

  const uploadFile = async (file: File | null, url: string, name: string) => {
    if (file && file.size) {
      const headers = {
        "Content-Type": "multipart/form-data",
      };
      const formData = new FormData();
      formData.append(name, file);
      return await axios.post(url, formData, {
        headers,
      });
    }
    return null;
  };

  const onSubmit = async (data: SharedInsightsProductInput) => {
    // because an empty filelist is initialized we need to filter out the files with name "undefined".
    const fileNames = Array.from(data?.files ?? [])
      .filter((f) => f.name !== "undefined")
      .map((f) => f.name);

    const PDFFile = data.files?.[0] || null;

    try {
      await uploadFile(PDFFile, "/upload/product/insights-content", "file");
    } catch (err) {
      console.log(err);
      toast({
        title: "Error",
        description: "An error occurred during report file upload.",
        status: "error",
      });
    }

    if (PDFFile && PDFFile.size) {
      const url = "/upload/product/insights-content";
      const headers = {
        "Content-Type": "multipart/form-data",
      };
      const formData = new FormData();
      try {
        await axios.post(url, formData, {
          headers,
        });
      } catch (err) {
        console.log(err);
        toast({
          title: "Error",
          description: "An error occurred during file upload.",
          status: "error",
        });
        return;
      }
    }

    try {
      const mutationResponse = await mutation({
        variables: {
          product: {
            ...data,
            ...marketplaceFlags,
            files: fileNames,
            proprietaryDataset: productType,
            details: {
              ...data.details,
              teaserQuotes: data.details.teaserQuotes.filter(
                (quote) => !!quote.trim()
              ),
              eventStartDate: data.details.eventStartDate
                ? moment(data.details.eventStartDate).format("MM/DD/YYYY")
                : null,
              eventEndDate: data.details.eventEndDate
                ? moment(data.details.eventEndDate).format("MM/DD/YYYY")
                : null,
            },
          },
        },
      });

      if (!mutationResponse?.data) {
        toastUnknownError();
        return;
      }

      const responseData = getResponse(mutationResponse?.data);

      if (!responseData || responseData.error) {
        toast({
          title: "Error",
          description: responseData?.error || "An unknown error occurred.",
          status: "error",
        });
        return;
      }

      try {
        if (productImageFile) {
          const res = await uploadFile(
            productImageFile,
            `/upload/product/image/${responseData.product.id}`,
            "image"
          );
          await client.mutate({
            mutation: gql`
              mutation updateProductImage($id: String!, $name: String!) {
                updateProductImage(id: $id, name: $name)
              }
            `,
            variables: {
              id: responseData.product.id,
              name: res?.data.fileName,
            },
          });
        }
      } catch (err) {
        console.log(err);
        toast({
          title: "Error",
          description: "An error occurred during image file upload.",
          status: "error",
        });
        return;
      }

      toast.closeAll();
      history.push(`/products?q=${responseData.product.id}`);
    } catch (err) {
      toastUnknownError();
      console.log(err);
    }
  };

  const mapTopicFields = (opts: TopicSelectValue | null) => {
    if (!opts) return [];

    return opts?.map((opt) => {
      const key =
        opt.type === "Sector"
          ? "sectorId"
          : opt.type === "Vendor"
          ? "vendorId"
          : (null as never);

      return (
        {
          name: opt.name,
          slug: opt.slug,
          [key]: opt.id,
        } || []
      );
    });
  };

  return (
    <Box
      height="100%"
      sx={{
        ".react-datepicker-wrapper": { width: "100%" },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={4}>
          <FormControl isRequired isInvalid={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              {...register("name", { required: "Name is required" })}
              placeholder="Name"
              data-cy="name"
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isRequired isInvalid={!!errors.description}>
            <FormLabel>Description</FormLabel>
            <Textarea
              {...register("description", {
                required: "Description is required",
              })}
              placeholder="Description"
              data-cy="description"
            />
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.previewDescription}>
            <FormLabel>Preview Description</FormLabel>
            <Input
              {...register("previewDescription", {
                required: "Preview Description is required",
              })}
              placeholder="Preview Description"
              data-cy="previewDescription"
            />
            <FormErrorMessage>
              {errors.previewDescription?.message}
            </FormErrorMessage>
          </FormControl>
          <FormControl isRequired isInvalid={!!errors.publishDate}>
            <FormLabel>Publish Date</FormLabel>
            <Controller
              rules={{ required: "Publish Date is required" }}
              name="publishDate"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <ReactDatePicker
                  placeholderText={moment().format("MM/DD/YYYY").toString()}
                  showPopperArrow={false}
                  customInput={<Input />}
                  onChange={onChange}
                  onBlur={onBlur}
                  selected={new Date(value)}
                />
              )}
            />
            <FormErrorMessage>{errors.publishDate?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.details?.tags}>
            <FormLabel>Tags</FormLabel>
            <Controller
              name="details.tags"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value?.map((v) => ({ label: v, value: v }))}
                  onChange={(tags) => field.onChange(tags?.map((t) => t.value))}
                  isMulti
                />
              )}
            />
            <FormErrorMessage>
              {errors.details?.tags?.[0]?.message}
            </FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors.vendors}>
            <FormLabel>Vendors</FormLabel>
            <Controller
              name="vendors"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <TopicSelect
                    type="Vendor"
                    value={value}
                    onChange={(value) => onChange(mapTopicFields(value))}
                  />
                );
              }}
            />
          </FormControl>
          <FormControl isInvalid={!!errors.sectors}>
            <FormLabel>Sectors</FormLabel>
            <Controller
              name="sectors"
              control={control}
              render={({ field: { onChange, value } }) => {
                return (
                  <TopicSelect
                    type="Sector"
                    value={value}
                    onChange={(value) => onChange(mapTopicFields(value))}
                  />
                );
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Event Details</FormLabel>
            <Box border="1px #d9d9d9 solid" borderRadius={5} p="1rem">
              <Stack spacing={4}>
                <FormControl isInvalid={!!errors.details?.eventCount}>
                  <FormLabel>Events Cited</FormLabel>
                  <Input
                    {...register("details.eventCount", {
                      pattern: {
                        value: /^\d+$/,
                        message: "Events Cited must be a positive integer",
                      },
                    })}
                    placeholder="0"
                    m={0}
                  />
                  <FormErrorMessage>
                    {errors.details?.eventCount?.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.details?.pageCount}>
                  <FormLabel>Pages in Report</FormLabel>
                  <Input
                    {...register("details.pageCount", {
                      pattern: {
                        value: /^\d+$/,
                        message: "Pages in Report must be a positive integer",
                      },
                    })}
                    placeholder="0"
                    m={0}
                  />
                  <FormErrorMessage>
                    {errors.details?.pageCount?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormLabel>Report Time Frame</FormLabel>
                <FormControl isInvalid={!!errors.details?.eventStartDate}>
                  <HStack spacing={4}>
                    <Box w="100%">
                      <Controller
                        name="details.eventStartDate"
                        control={control}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <ReactDatePicker
                            placeholderText={moment()
                              .format("MM/DD/YYYY")
                              .toString()}
                            showPopperArrow={false}
                            customInput={<Input />}
                            onChange={onChange}
                            onBlur={onBlur}
                            selected={value ? moment(value).toDate() : null}
                          />
                        )}
                      />
                    </Box>
                    <Box w="100%">
                      <Controller
                        name="details.eventEndDate"
                        control={control}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <ReactDatePicker
                            placeholderText={moment()
                              .add(1, "day")
                              .format("MM/DD/YYYY")
                              .toString()}
                            showPopperArrow={false}
                            customInput={<Input />}
                            onChange={onChange}
                            onBlur={onBlur}
                            selected={value ? moment(value).toDate() : null}
                          />
                        )}
                      />
                    </Box>
                  </HStack>
                </FormControl>
              </Stack>
            </Box>
          </FormControl>
          <FormControl isInvalid={!!errors.files}>
            <FormLabel>File</FormLabel>
            <Input
              accept="application/pdf"
              type="file"
              {...fileRegisterRest}
              hidden
              ref={(e) => {
                fileCallbackRef(e);
                fileInputRef.current = e;
              }}
            />
            <HStack spacing={4}>
              <Button onClick={() => fileInputRef.current?.click()}>
                Choose File
              </Button>
              <Text>
                {watchFiles?.[0]?.name && watchFiles[0].name !== "undefined"
                  ? watchFiles[0].name
                  : "No file chosen"}
              </Text>
            </HStack>
            <FormErrorMessage>{errors.files?.[1]?.message}</FormErrorMessage>
          </FormControl>
          <FormControl>
            <FormLabel>Teaser Quote #1</FormLabel>
            <Textarea
              {...register("details.teaserQuotes.0")}
              type="textarea"
              placeholder="Quote text"
              data-cy="teaserQuote1"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Teaser Quote #2</FormLabel>
            <Textarea
              {...register("details.teaserQuotes.1")}
              type="textarea"
              placeholder="Quote text"
              data-cy="teaserQuote2"
            />
          </FormControl>

          <Button colorScheme="blue" type="submit">
            Submit
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

const createSharedInsightsProductMutation = gql`
  mutation CreateSharedInsightsProduct($product: SharedInsightsProductInput!) {
    createSharedInsightsProduct(product: $product) {
      product {
        ...AdminSharedInsightsProductInfo
      }
      error
    }
  }
  ${AdminSharedInsightsProductInfo}
`;

const updateSharedInsightsProductMutation = gql`
  mutation UpdateSharedInsightsProduct(
    $id: String!
    $product: SharedInsightsProductInput!
  ) {
    updateSharedInsightsProduct(id: $id, product: $product) {
      product {
        ...AdminSharedInsightsProductInfo
      }
      error
    }
  }
  ${AdminSharedInsightsProductInfo}
`;

type CreateUpdateInsightsResponse = {
  product: Product;
  error: string;
};

type SharedInsightsProductInputWithoutFileList = Omit<
  SharedInsightsProductInput,
  "files"
> & {
  files: string[];
};

type CreateUpdateSharedInsightsMutationResult =
  | {
      createSharedInsightsProduct: CreateUpdateInsightsResponse;
    }
  | {
      updateSharedInsightsProduct: CreateUpdateInsightsResponse;
    };

type CreateUpdateSharedInsightsMutation = MutationFn<
  CreateUpdateSharedInsightsMutationResult,
  {
    product: SharedInsightsProductInputWithoutFileList;
  }
>;

type SharedProductFormWithDataProps = Omit<
  SharedProductFormProps,
  "mutation" | "client"
>;

export default withRouter<
  SharedProductFormWithDataProps,
  React.ComponentType<SharedProductFormWithDataProps>
>(
  withApollo((props) => {
    const id = props.product?.id;
    return (
      <Mutation
        mutation={
          id
            ? updateSharedInsightsProductMutation
            : createSharedInsightsProductMutation
        }
        variables={id ? { id } : undefined}
      >
        {(mutation: CreateUpdateSharedInsightsMutation) => (
          <SharedProductForm
            {...props}
            mutation={mutation}
            client={props.client}
          />
        )}
      </Mutation>
    );
  })
);
